import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  RiFileTextLine, 
  RiDownloadLine, 
  RiSendPlaneFill,
  RiFileListLine,
  RiCalendarEventLine,
  RiTimeLine,
  RiMailLine,
  RiGlobalLine,
  RiInformationLine,
  RiFileCopyLine,
  RiShieldCheckLine,
  RiAlarmLine,
  RiCalendarLine,
  RiMicroscopeLine,
  RiUploadCloudLine,
  RiFilePdfLine,
  RiPresentationLine,
  RiCheckLine,
  RiArrowDownSLine,
} from 'react-icons/ri';
import toast, { Toaster } from 'react-hot-toast';
import axios from 'axios';
import { API_BASE } from '../config';

axios.defaults.baseURL = API_BASE;

// ─── Theme (unchanged) ─────────────────────────────────────────────
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
};

// ─── CSS (unchanged) ──────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(2rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.88rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:14px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0}
  .t-st{background:${T.white};padding:20px 28px;display:flex;flex-direction:column;gap:4px;min-width:100px}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:11px;color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.08em}

  .t-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:28px;box-shadow:${T.shadow};transition:all 0.3s ease}
  .t-card:hover{transform:translateY(-4px);box-shadow:${T.shadowLg}}
  .t-card-header{display:flex;align-items:center;gap:12px;margin-bottom:20px}
  .t-card-icon{width:44px;height:44px;border-radius:50%;background:${T.softTeal};display:flex;align-items:center;justify-content:center}
  .t-card-title{font-size:20px;font-weight:800;color:${T.text}}

  .t-form-input{width:100%;padding:14px 18px;background:${T.offWhite};border:1px solid ${T.borderLight};border-radius:14px;font-size:14px;color:${T.text};transition:all 0.2s;outline:none}
  .t-form-input:focus{border-color:${T.primary};background:${T.white}}
  .t-form-label{display:block;font-size:13px;font-weight:600;color:${T.textMid};margin-bottom:8px}
  .t-required{color:#EF4444}

  .t-btn-primary{background:${T.gradGold};color:${T.navyDeep};border:none;padding:14px 32px;border-radius:50px;font-size:15px;font-weight:700;cursor:pointer;transition:all 0.2s}
  .t-btn-primary:hover:not(:disabled){transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-btn-outline{background:${T.white};color:${T.primary};border:1px solid ${T.borderLight};padding:10px 24px;border-radius:40px;font-size:14px;font-weight:600;cursor:pointer;transition:all 0.2s;display:inline-flex;align-items:center;gap:8px}
  .t-btn-outline:hover{background:${T.primary};color:${T.white};border-color:${T.primary}}

  .t-topic-tag{background:${T.softTeal};color:${T.primary};padding:6px 16px;border-radius:30px;font-size:13px;font-weight:500;transition:all 0.2s}
  .t-dropzone{border:2px dashed ${T.borderLight};border-radius:16px;padding:24px;text-align:center;background:${T.offWhite};transition:all 0.2s;cursor:pointer}
  .t-dropzone:hover{border-color:${T.primary};background:${T.softTeal}}
  .t-dropzone-active{border-color:${T.primary};background:${T.softTeal}}

  .t-footer{border-top:1px solid ${T.border};background:${T.white};padding:28px clamp(20px,4vw,64px);margin-top:40px}
  .t-fi{max-width:1320px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px}
  .t-f-copy{font-size:12px;color:${T.textMuted};letter-spacing:.01em}

  @keyframes spin{to{transform:rotate(360deg)}}
  .t-spinner{width:18px;height:18px;border:2px solid ${T.navyDeep};border-top-color:transparent;border-radius:50%;animation:spin 0.8s linear infinite}
`;

const guidelines = [
  { title: "Abstract Length", desc: "Maximum 500 words (excluding title and authors)", icon: RiFileTextLine },
  { title: "Language", desc: "English only — clear, concise scientific writing", icon: RiGlobalLine },
  { title: "Structure", desc: "Title, Authors, Affiliations, Body, Keywords, References", icon: RiFileListLine },
  { title: "Figures/Tables", desc: "Maximum 2 figures or tables (optional)", icon: RiInformationLine },
  { title: "Formatting", desc: "Times New Roman, 12pt, single-spaced", icon: RiFileCopyLine },
  { title: "Originality", desc: "Must be original, unpublished work", icon: RiShieldCheckLine },
];

const importantDates = [
  { event: "Abstract Submission Opens", date: "January 15, 2026", status: "open", icon: RiCalendarEventLine },
  { event: "Early Bird Registration", date: "March 30, 2026", status: "upcoming", icon: RiAlarmLine },
  { event: "Abstract Submission Deadline", date: "May 15, 2026", status: "deadline", icon: RiTimeLine },
  { event: "Notification of Acceptance", date: "June 30, 2026", status: "upcoming", icon: RiMailLine },
  { event: "Final Program Release", date: "July 30, 2026", status: "upcoming", icon: RiCalendarLine },
  { event: "Conference Dates", date: "August 15-17, 2026", status: "conference", icon: RiCalendarEventLine },
];

// ─── CUSTOM MULTI-SELECT COMPONENT ──────────────────────────────
const MultiSelect = ({ options, selected, onChange, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value) => {
    if (disabled) return;
    const newSelected = selected.includes(value)
      ? selected.filter(id => id !== value)
      : [...selected, value];
    onChange(newSelected);
  };

  const selectedNames = options
    .filter(opt => selected.includes(opt._id))
    .map(opt => opt.name);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 18px',
          background: T.offWhite,
          border: `1px solid ${T.borderLight}`,
          borderRadius: 14,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          minHeight: '52px',
          flexWrap: 'wrap',
          gap: '6px',
        }}
        className="t-form-input"
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', flex: 1 }}>
          {selectedNames.length > 0 ? (
            selectedNames.map((name, idx) => (
              <span
                key={idx}
                style={{
                  background: T.softTeal,
                  color: T.primary,
                  padding: '2px 12px',
                  borderRadius: 20,
                  fontSize: 13,
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {name}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    const id = options.find(o => o.name === name)?._id;
                    if (id) toggleOption(id);
                  }}
                  style={{
                    cursor: 'pointer',
                    marginLeft: 4,
                    fontSize: 14,
                    lineHeight: 1,
                    color: T.primary,
                  }}
                >
                  ×
                </span>
              </span>
            ))
          ) : (
            <span style={{ color: T.textMuted, fontSize: 14 }}>{placeholder || 'Select...'}</span>
          )}
        </div>
        <RiArrowDownSLine
          size={20}
          style={{
            color: T.textMuted,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s',
            flexShrink: 0,
          }}
        />
      </div>

      {isOpen && !disabled && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: T.white,
            border: `1px solid ${T.borderLight}`,
            borderRadius: 14,
            boxShadow: T.shadowMd,
            maxHeight: 200,
            overflowY: 'auto',
            zIndex: 100,
            padding: '8px 0',
          }}
        >
          {options.map(option => {
            const isSelected = selected.includes(option._id);
            return (
              <div
                key={option._id}
                onClick={() => toggleOption(option._id)}
                style={{
                  padding: '10px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  background: isSelected ? T.softTeal : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = T.offWhite;
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.background = 'transparent';
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {}}
                  style={{ accentColor: T.primary, width: 16, height: 16, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 14, color: T.text }}>{option.name}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────
export default function Abstract() {
  const [topics, setTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submissionData, setSubmissionData] = useState({
    fullName: '',
    email: '',
    abstractTitle: '',
    presentationType: '',
    topics: [],
  });
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    affiliation: '',
    abstractTitle: '',
    presentationType: 'plenary',
    topics: [],
    mathAnswer: '',
    supportingFiles: null
  });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  // Fetch topics
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await axios.get('/api/summit-topics');
        if (response.data.success && response.data.summitTopics) {
          setTopics(response.data.summitTopics);
        } else {
          setTopics([
            { _id: "1", name: "Adaptive Immunity" },
            { _id: "2", name: "Innate Immunity" },
            { _id: "3", name: "Autoimmunity & Inflammatory Disorders" },
            { _id: "4", name: "Cancer Immunology & Immunotherapy" },
            { _id: "5", name: "Vaccine Development" },
            { _id: "6", name: "Clinical Immunology" }
          ]);
        }
      } catch (error) {
        console.error('Error fetching topics:', error);
        setTopics([
          { _id: "1", name: "Adaptive Immunity" },
          { _id: "2", name: "Innate Immunity" },
          { _id: "3", name: "Autoimmunity & Inflammatory Disorders" },
          { _id: "4", name: "Cancer Immunology & Immunotherapy" },
          { _id: "5", name: "Vaccine Development" },
          { _id: "6", name: "Clinical Immunology" }
        ]);
        toast.error('Could not load topics, using default list');
      } finally {
        setLoadingTopics(false);
      }
    };
    fetchTopics();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'supportingFiles' && files) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        setFormData(prev => ({ ...prev, [name]: file }));
        setUploadedFile(file);
      } else {
        toast.error('Only PDF files are allowed');
        e.target.value = '';
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTopicsChange = (selectedIds) => {
    setFormData(prev => ({ ...prev, topics: selectedIds }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (parseInt(formData.mathAnswer) !== 19) {
      toast.error('Please solve the math question correctly');
      return;
    }
    if (!formData.fullName || !formData.email || !formData.abstractTitle) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (formData.topics.length === 0) {
      toast.error('Please select at least one topic');
      return;
    }
    if (!agreed) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('fullName', formData.fullName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('affiliation', formData.affiliation);
      submitData.append('abstractTitle', formData.abstractTitle);
      submitData.append('presentationType', formData.presentationType);
      submitData.append('mathAnswer', parseInt(formData.mathAnswer));
      submitData.append('agreed', agreed);
      submitData.append('topics', JSON.stringify(formData.topics));
      if (formData.supportingFiles) {
        submitData.append('supportingFiles', formData.supportingFiles);
      }
      
      const response = await axios.post('/api/abstracts/submit', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        toast.success('Abstract submitted successfully!');
        const selectedTopicNames = topics
          .filter(t => formData.topics.includes(t._id))
          .map(t => t.name);
        setSubmissionData({
          fullName: formData.fullName,
          email: formData.email,
          abstractTitle: formData.abstractTitle,
          presentationType: formData.presentationType,
          topics: selectedTopicNames,
        });
        setSubmitted(true);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          affiliation: '',
          abstractTitle: '',
          presentationType: 'plenary',
          topics: [],
          mathAnswer: '',
          supportingFiles: null
        });
        setUploadedFile(null);
        setAgreed(false);
        const fileInput = document.getElementById('supportingFiles');
        if (fileInput) fileInput.value = '';
      } else {
        toast.error(response.data.message || 'Submission failed.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.message || 'Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetSubmission = () => {
    setSubmitted(false);
    setSubmissionData({ fullName: '', email: '', abstractTitle: '', presentationType: '', topics: [] });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDownloadTemplate = () => {
    const templateContent = `...`; // (same as before, omitted for brevity)
    const blob = new Blob([templateContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'meeting21_abstract_template.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Template downloaded successfully!');
  };

  const totalGuidelines = guidelines.length;

  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>
      <Toaster position="top-right" toastOptions={{
        duration: 4000,
        style: { background: T.navy, color: T.white, borderRadius: '12px' },
        success: { duration: 3000 },
        error: { duration: 4000 },
      }} />

      {/* Hero section */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div>
            <div className="t-hero-lbl">
              <RiPresentationLine size={13} />
              Meeting21 · Call for Abstracts
            </div>
            <h1 className="t-hero-h">Abstract <em>Submission</em></h1>
            <p className="t-hero-p">
              All abstracts will be peer‑reviewed by the Scientific Committee. Selection will be based on scientific quality, originality, and relevance to the conference theme.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st"><span className="t-st-n">{totalGuidelines}</span><span className="t-st-l">Guidelines</span></div>
            <div className="t-st"><span className="t-st-n">500</span><span className="t-st-l">Words Max</span></div>
            <div className="t-st"><span className="t-st-n">2027</span><span className="t-st-l">Edition</span></div>
          </div>
        </div>
      </section>

      {/* Main content */}
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '40px clamp(20px,4vw,64px) 80px' }}>
        
        {/* Guidelines & Dates */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, marginBottom: 48 }}>
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="t-card">
            <div className="t-card-header">
              <div className="t-card-icon"><RiFileListLine size={24} style={{ color: T.primary }} /></div>
              <h2 className="t-card-title">Submission Guidelines</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {guidelines.map((guide, index) => (
                <div key={index} style={{ display: 'flex', gap: 12 }}>
                  <guide.icon size={20} style={{ color: T.primary, marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 }}>{guide.title}</h3>
                    <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.4 }}>{guide.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="t-card">
            <div className="t-card-header">
              <div className="t-card-icon"><RiCalendarEventLine size={24} style={{ color: T.primary }} /></div>
              <h2 className="t-card-title">Important Dates</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {importantDates.map((date, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: index < importantDates.length - 1 ? `1px solid ${T.borderLight}` : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <date.icon size={18} style={{ color: date.status === 'open' ? T.primary : date.status === 'deadline' ? T.accentGold : T.textMuted }} />
                    <span style={{ fontSize: 14, color: T.text, fontWeight: 500 }}>{date.event}</span>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: date.status === 'open' ? T.primary : date.status === 'deadline' ? T.accentGold : T.textMuted }}>{date.date}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Topics of Interest (display only) */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="t-card" style={{ marginBottom: 48 }}>
          <div className="t-card-header">
            <div className="t-card-icon"><RiMicroscopeLine size={24} style={{ color: T.primary }} /></div>
            <h2 className="t-card-title">Topics of Interest</h2>
          </div>
          {loadingTopics ? (
            <div style={{ textAlign: 'center', padding: '20px' }}><div className="t-spinner" style={{ borderColor: `${T.border} transparent transparent transparent`, width: 30, height: 30 }} /></div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {topics.map((topic, idx) => (
                <span key={topic._id || idx} className="t-topic-tag">{topic.name}</span>
              ))}
            </div>
          )}
        </motion.div>

        {/* Download Template */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <motion.button onClick={handleDownloadTemplate} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="t-btn-outline">
            <RiDownloadLine size={18} /> Download Abstract Template
          </motion.button>
        </div>

        {/* Submission Form / Success */}
        {submitted ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="t-card" style={{ padding: '40px', border: `2px solid ${T.primary}` }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: T.gradSuccess, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <RiCheckLine size={36} color={T.white} />
              </div>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 800, color: T.text, marginBottom: 6 }}>Your Abstract Submission is Successful</h2>
              <p style={{ fontSize: 16, color: T.textMid }}>Hello <strong>{submissionData.fullName}</strong>,</p>
            </div>
            <div style={{ background: T.offWhite, borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <p style={{ fontSize: 14, color: T.textMid }}>We have successfully received your abstract submission.</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 14, color: T.text }}>
              <div><strong>Conference:</strong> Immunology Summit 2026</div>
              <div><strong>Title:</strong> {submissionData.abstractTitle}</div>
              <div><strong>Full Name:</strong> {submissionData.fullName}</div>
              <div><strong>Email:</strong> {submissionData.email}</div>
              <div><strong>Presentation:</strong> {submissionData.presentationType.charAt(0).toUpperCase() + submissionData.presentationType.slice(1)}</div>
              <div><strong>Topics:</strong> {submissionData.topics?.join(', ') || 'None'}</div>
              <div><strong>Date:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <p style={{ fontSize: 14, color: T.textMuted, marginBottom: 16 }}>You will receive notification by <strong>June 30, 2026</strong>.</p>
              <button onClick={resetSubmission} className="t-btn-primary">Submit Another Abstract</button>
            </div>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="t-card" style={{ padding: '44px' }}>
            <div style={{ marginBottom: 36 }}>
              <h2 style={{ fontSize: 28, fontWeight: 800, color: T.text, marginBottom: 8 }}>Submit Your Abstract</h2>
              <p style={{ fontSize: 14, color: T.textMuted }}>All fields marked with <span className="t-required">*</span> are required</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24, marginBottom: 24 }}>
                <div>
                  <label className="t-form-label">Full Name <span className="t-required">*</span></label>
                  <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required disabled={loading} className="t-form-input" placeholder="Dr. John Smith" />
                </div>
                <div>
                  <label className="t-form-label">Email Address <span className="t-required">*</span></label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={loading} className="t-form-input" placeholder="john.smith@university.edu" />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24, marginBottom: 24 }}>
                <div>
                  <label className="t-form-label">Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} disabled={loading} className="t-form-input" placeholder="+1 (555) 123-4567" />
                </div>
                <div>
                  <label className="t-form-label">Affiliation/Institution</label>
                  <input type="text" name="affiliation" value={formData.affiliation} onChange={handleChange} disabled={loading} className="t-form-input" placeholder="Harvard Medical School" />
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label className="t-form-label">Abstract Title <span className="t-required">*</span></label>
                <input type="text" name="abstractTitle" value={formData.abstractTitle} onChange={handleChange} required disabled={loading} className="t-form-input" placeholder="A concise and descriptive title" />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label className="t-form-label">Presentation Type <span className="t-required">*</span></label>
                <select name="presentationType" value={formData.presentationType} onChange={handleChange} required disabled={loading} className="t-form-input" style={{ cursor: 'pointer' }}>
                  <option value="plenary">Plenary</option>
                  <option value="keynote">Keynote</option>
                  <option value="invited">Invited</option>
                  <option value="oral">Oral</option>
                  <option value="poster">Poster</option>
                </select>
              </div>

              {/* ─── CUSTOM MULTI-SELECT FOR TOPICS ─── */}
              <div style={{ marginBottom: 24 }}>
                <label className="t-form-label">Select Topics of Interest <span className="t-required">*</span></label>
                {loadingTopics ? (
                  <div style={{ padding: '10px', color: T.textMuted }}>Loading topics...</div>
                ) : (
                  <MultiSelect
                    options={topics}
                    selected={formData.topics}
                    onChange={handleTopicsChange}
                    placeholder="Select topics..."
                    disabled={loading}
                  />
                )}
                {formData.topics.length === 0 && (
                  <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4 }}>Please select at least one topic</p>
                )}
              </div>

              <div style={{ marginBottom: 24 }}>
                <label className="t-form-label">Upload Supporting Document (PDF) <span className="t-required">*</span></label>
                <div className={`t-dropzone ${uploadedFile ? 't-dropzone-active' : ''}`} onClick={() => document.getElementById('supportingFiles').click()}>
                  <input type="file" id="supportingFiles" name="supportingFiles" onChange={handleChange} accept=".pdf" required disabled={loading} style={{ display: 'none' }} />
                  {uploadedFile ? (
                    <div>
                      <RiFilePdfLine size={40} style={{ color: T.primary, marginBottom: 8 }} />
                      <p style={{ fontSize: 14, fontWeight: 600, color: T.primary, marginBottom: 4 }}>{uploadedFile.name}</p>
                      <p style={{ fontSize: 12, color: T.textMuted }}>{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setUploadedFile(null); setFormData(prev => ({ ...prev, supportingFiles: null })); }} style={{ marginTop: 8, padding: '4px 12px', background: '#EF4444', color: 'white', border: 'none', borderRadius: 20, fontSize: 12, cursor: 'pointer' }}>Remove</button>
                    </div>
                  ) : (
                    <div>
                      <RiUploadCloudLine size={40} style={{ color: T.textMuted, marginBottom: 8 }} />
                      <p style={{ fontSize: 14, color: T.textMuted, marginBottom: 4 }}>Click or drag & drop to upload PDF</p>
                      <p style={{ fontSize: 12, color: T.textMuted }}>Maximum file size: 10MB</p>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: 24, padding: '20px', background: T.softTeal, borderRadius: 16, border: `1px solid ${T.borderLight}` }}>
                <label className="t-form-label">Security Check: What's 16 + 3? <span className="t-required">*</span></label>
                <input type="number" name="mathAnswer" value={formData.mathAnswer} onChange={handleChange} required disabled={loading} className="t-form-input" style={{ background: T.white }} placeholder="Enter your answer" />
              </div>

              <div style={{ marginBottom: 32 }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: loading ? 'not-allowed' : 'pointer' }}>
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} disabled={loading} style={{ marginTop: 2, accentColor: T.primary }} />
                  <span style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.5 }}>
                    I confirm that this abstract is original work and has not been published or presented elsewhere. 
                    I agree to all policies and terms and permit my abstract to be included in the conference proceedings 
                    and website if accepted.
                  </span>
                </label>
              </div>

              <motion.button type="submit" disabled={loading} className="t-btn-primary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                {loading ? <><div className="t-spinner" /> Submitting...</> : <>Submit Abstract <RiSendPlaneFill size={18} /></>}
              </motion.button>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
}